import Breadcrumb from "Components/UI/Breadcrumb";
import Spinners from "Components/UI/Spinner";
import PaginatedTable from "Components/Tables/PaginatedTable";
import React, { useEffect, useState } from "react";
import { Card, CardBody, Container } from "reactstrap";
import useTableFilters from "helpers/hooks/useTableFilters";
import { generateQueryParams } from "helpers/queryParams";
import convertDateFormat from "Components/Date/DateFormat";
import DeleteModal from "Components/Modals/DeleteModal";
import { createPackageGroup, deletePackageGroup, getPackageGroups, updatePackageGroup } from "helpers/API Calls/Operations/packages";
import { getProducts } from "helpers/API Calls/Operations/Products";
import ModalWrapper from "Components/Modals/ModalWrapper";
import Select from "react-select";
import { ProductData } from "types/Product";

interface PackageGroup {
  id: number;
  group_name: string;
  products: Array<{
    id: string;
    product_name: string;
  }>;
  created_at: string;
  updated_at: string;
}

interface Pagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

interface SelectOption {
  label: string;
  value: string;
}

const head = [
  { id: 1, name: "#", isSortable: true, key: "id" },
  { id: 2, name: "Name", isSortable: true, key: "group_name" },
  { id: 4, name: "Products", isSortable: false },
  { id: 9, name: "Created", isSortable: true, key: "created_at" },
  { id: 10, name: "Updated", isSortable: true, key: "updated_at" },
  { id: 11, name: "Actions", isSortable: false },
]

function GroupsListing() {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<PackageGroup[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  });

  const [products, setProducts] = useState<SelectOption[]>([]);
  const [modal, setModal] = useState<boolean>(false); 
  const [selectedProducts, setSelectedProducts] = useState<SelectOption[] | null>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<PackageGroup | null>(null);
  
  const { filters, updateFilter } = useTableFilters();

  const fetchData = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getPackageGroups(generateQueryParams(filters));
      if (response.success) {
        setData(response.data.items);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getProducts('limit=1000&is_addon=0');
        if (response.success) {
          setProducts(response.data?.items?.map((item: ProductData) => ({
            value: item.id,
            label: item.product_name
          })));
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    }

    fetchData();
    fetchProducts();
  }, [fetchData]);

  const handleDeleteClick = (item: PackageGroup) => {
    setSelectedItem(item);
    setDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedItem) {
      const response = await deletePackageGroup(selectedItem.id);
      if (response.success) {
        setDeleteModal(false);
        setSelectedItem(null);
        setIsLoading(true);
        await fetchData();
        setIsLoading(false);
      }
    }
  };

  const onCloseClick = () => {
    setDeleteModal(false);
    setSelectedItem(null);
  };

    const handleFromSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const data = {
      group_name: formData.get('group_name') as string,
      products: selectedProducts?.map(p => p.value) || [],
    }
    
    try {
      const response = selectedItem 
        ? await updatePackageGroup(selectedItem.id, data) 
        : await createPackageGroup(data);
      
      if (response.success) {
        setModal(false);
        setSelectedItem(null);
        setSelectedProducts(null);
        await fetchData();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  }

  useEffect(() => {
    if (selectedItem) {
      // When editing, set the selected products based on the item's products
      const selectedProductOptions = selectedItem.products.map(product => ({
        value: product.id,
        label: product.product_name
      }));
      setSelectedProducts(selectedProductOptions);
    } else {
      setSelectedProducts(null);
    }
  }, [selectedItem]);

  return (
    <>
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Packages"},]} />
        {isLoading ? <div className="position-relative vh-100"><Spinners/></div>
        :
        <Card>
          <CardBody>
            <PaginatedTable
              tableHead={head}
              pagination={pagination}
              isAddModal={true}
              buttonText="Add New Group"
              onAddClick={() => {setModal(true)}}
              updateFilter={updateFilter}
              filters={filters}
            > 
              {data.length > 0 && data.map((item) => (
                  <tr key={item?.id}>
                    <td>{item?.id}</td>
                    <td>{item?.group_name}</td>
                    <td>{item?.products?.length}</td>
                    <td>{convertDateFormat(item.created_at)}</td>
                    <td>{convertDateFormat(item.updated_at)}</td>
                    <td>
                      <div>
                        <button className="btn btn-primary btn-sm me-1" onClick={() => {setSelectedItem(item); setModal(true); setSelectedProducts(item.products.map(p => ({ value: p.id, label: p.product_name })))}}>
                          <i className="mdi mdi-pencil"></i>
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteClick(item)}><i className="mdi mdi-trash-can-outline"></i></button>
                      </div>
                    </td>
                  </tr>))
                }
                {!isLoading && data.length === 0 && <tr className="text-center"><td colSpan={head.length} className="text-danger">No Data Found</td></tr>}
                
            </PaginatedTable>
          </CardBody>
        </Card>}
      </Container>
    </div>
    {deleteModal && <DeleteModal
      show={deleteModal}
      onDeleteClick={handleDeleteConfirm}
      onCloseClick={onCloseClick}
      message={`${selectedItem?.group_name} group?`}
    />}
    {modal && <ModalWrapper
        show={modal}
        onCloseClick={() => {setModal(false); setSelectedItem(null)}}
        title={selectedItem ? "Edit Group" : "Add New Group"}
        body={
            <form onSubmit={handleFromSubmit} autoComplete="off">
                <div className="mb-3 form-group">
                    <label htmlFor="group_name" className="form-label">Group Name <span className="text-danger">*</span></label>
                    <input
                        type="text"
                        className="form-control"
                        id="group_name"
                        name="group_name"
                        defaultValue={selectedItem ? selectedItem.group_name : ""}
                        required
                    />
                    <Select
                      id="products"
                      name="products"
                      placeholder="Select Products"
                      options={products}
                      value={selectedProducts}
                      isClearable
                      isMulti
                      onChange={(value) => {
                        setSelectedProducts(value ? [...value] : null);
                      }}
                      className="mt-2"
                      styles={{ menu: base => ({ ...base, zIndex: 19999,}),}}
                    />
                </div>
                <div className="text-end">
                    <button type="button" className="btn btn-secondary me-2" onClick={() => {setModal(false); setSelectedItem(null)}}>Cancel</button>
                    <button type="submit" className="btn btn-primary">{selectedItem ? "Update Group" : "Add Group"}</button>
                </div>
            </form>
        }
        />}
    
    </>
  )
}

export default GroupsListing
